import {useMemo, useState, useEffect} from 'react'
import isEqual from 'lodash.isequal'
import {useSelector} from 'react-redux'
import {selectSettingsUsers} from 'features/Settings'
import {selectPolicies} from 'features/Policies/policiesSelectors'
import {
  selectedEndpointDebugMode,
  selectEndpoints,
} from 'features/Endpoints/endpointsSelectors'
import {EndpointsConfigTypes, ValidUserTypes} from 'interfaces'
import {selectUser} from 'features/User'
import {queryClient} from 'services'
import {
  useSnapshotContext,
  SETTINGS_ENDPOINT_CONFIG_SNAPSHOT,
} from 'context/SnapshotContext'

type ActionType = {
  isSnackbarActionAvailable: boolean
  doIHaveAccess: boolean
  isUserLoggedIn: boolean
}

export const useActions = (): ActionType => {
  const {policyAssignee, cloudApp} = useSelector(selectPolicies)
  const {role, current} = useSelector(selectUser)
  const {snapshot} = useSnapshotContext()

  const doIHaveAccess = useMemo(() => {
    return role === ValidUserTypes.Administrator
  }, [role])

  const {blockPages, defaultBlockPagePayload} = useSelector(selectSettingsUsers)
  const {selectedEndpoints} = useSelector(selectEndpoints)
  const isDebugMode = useSelector(selectedEndpointDebugMode)

  const isUserLoggedIn = useMemo(() => {
    return Boolean(current?.token)
  }, [current])

  const endpointSettingsConfigSnapshot = snapshot?.[
    SETTINGS_ENDPOINT_CONFIG_SNAPSHOT
  ] as EndpointsConfigTypes

  const [latestEndpointSettingsData, setLatestEndpointSettingsData] = useState(
    endpointSettingsConfigSnapshot,
  )

  useEffect(() => {
    const unsubscribe = queryClient.getQueryCache().subscribe(({query}) => {
      if (query.queryKey[0] === 'getEndpointsConfig') {
        const {
          state: {data: updatedCacheData},
        } = query
        setLatestEndpointSettingsData(updatedCacheData?.data)
      }
    })
    return () => unsubscribe()
  }, [queryClient])

  const isSnackbarActionAvailable = useMemo(() => {
    return (
      isDebugMode ||
      !isEqual(policyAssignee?.data, policyAssignee?.snapshot) ||
      (!isEqual(latestEndpointSettingsData, endpointSettingsConfigSnapshot) &&
        endpointSettingsConfigSnapshot?.requireDelegatedAuth) ||
      !isEqual(
        blockPages?.data?.pages?.default,
        blockPages?.snapshot?.pages?.default,
      ) ||
      (endpointSettingsConfigSnapshot &&
        !isEqual(latestEndpointSettingsData, endpointSettingsConfigSnapshot)) ||
      Boolean(defaultBlockPagePayload?.defaultPages?.length) ||
      (blockPages?.updatedData?.addCustomPages
        ? blockPages?.updatedData?.addCustomPages
        : []
      ).length > 0 ||
      (blockPages?.updatedData?.deleteCustomPages
        ? blockPages?.updatedData?.deleteCustomPages
        : []
      ).length > 0 ||
      !isEqual(cloudApp?.data, cloudApp?.snapshot)
    )
  }, [
    policyAssignee,
    latestEndpointSettingsData,
    endpointSettingsConfigSnapshot,
    blockPages,
    selectedEndpoints,
    defaultBlockPagePayload,
    cloudApp,
    role,
  ])

  return {
    isSnackbarActionAvailable,
    doIHaveAccess,
    isUserLoggedIn,
  }
}
